import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FiUsers,
  FiBookOpen,
  FiCalendar,
  FiBarChart2,
  FiDollarSign,
  FiTag,
  FiPackage,
  FiShoppingBag,
  FiSearch,
  FiTrash2,
  FiUpload,
  FiUserPlus,
  FiPlus,
  FiCheckCircle,
  FiArrowUpRight,
  FiBriefcase,
  FiUser,
  FiBell,
} from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';
import { useAuth } from '../../context/AuthContext';

/* =========================================================
   Refined-editorial admin dashboard
   - Hero with personalized greeting and date
   - Compact KPI strip
   - Inline quick actions
   - Management grid (with live counts where available)
   ========================================================= */

const Page = styled.div`
  padding: 2.5rem 2rem 4rem;
  max-width: 1240px;
  margin: 0 auto;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;

  @media (max-width: 768px) { padding: 1.5rem 1rem 3rem; }
  @media (max-width: 480px) { padding: 1.25rem 0.875rem 2.5rem; }
`;

/* ---------- Hero ---------- */
const Hero = styled.section`
  margin-bottom: 2.5rem;
`;

const HeroDivider = styled.div`
  height: 1px;
  background: var(--border-color);
  margin: 2rem 0 2.5rem 0;
`;

const HeroEyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.075em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
`;

const HeroTitle = styled.h1`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: clamp(1.5rem, 2vw + 0.65rem, 2rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--text-primary);
  margin: 0 0 0.75rem 0;
  line-height: 1.1;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: clamp(1.25rem, 2vw + 0.45rem, 1.6rem);
  }
`;

const HeroGreetingAr = styled.span`
  font-family: 'Noto Sans Arabic', 'Amiri', 'Scheherazade New', 'Arial Unicode MS', sans-serif;
  font-weight: 600;
  font-size: 1em;
  letter-spacing: 0;
  direction: rtl;
  unicode-bidi: isolate;
  display: inline;
`;

const HeroGreetingName = styled.span`
  font-weight: 500;
  color: var(--text-secondary);
  letter-spacing: -0.015em;
  display: inline;
`;

const HeroSub = styled.p`
  font-size: 1rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.5;
  max-width: none;
  white-space: nowrap;

  @media (max-width: 768px) {
    white-space: normal;
  }
`;

/* ---------- Brand-cohesive accent palette ---------- */
/* Earthy, desaturated tones that complement the warm gold primary without
   competing with it. Each section item picks one slot. */
const ACCENT = {
  gold:       { tint: 'rgba(184, 149, 106, 0.12)', solid: '#B8956A' },
  indigo:     { tint: 'rgba(91, 107, 161, 0.12)',  solid: '#5B6BA1' },
  sage:       { tint: 'rgba(107, 142, 127, 0.14)', solid: '#5C8676' },
  terracotta: { tint: 'rgba(178, 125, 92, 0.14)',  solid: '#A86A4A' },
  teal:       { tint: 'rgba(79, 130, 144, 0.12)',  solid: '#3E7A82' },
  plum:       { tint: 'rgba(126, 95, 126, 0.14)',  solid: '#7B5E7D' },
  amber:      { tint: 'rgba(168, 146, 84, 0.14)',  solid: '#9A8344' },
}

/* ---------- KPI Strip ---------- */
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2.75rem;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr 1fr; gap: 0.625rem; }
`;

const Kpi = styled(motion.div)`
  position: relative;
  background: var(--background-primary);
  border: 2px solid ${(p) => p.$accentColor || 'var(--border-color)'};
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  overflow: hidden;

  /* Bold accent strip along the left edge */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: ${(p) => p.$accent || 'transparent'};
    opacity: 1;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px ${(p) => p.$accentColor || 'var(--border-color)'};
    border-color: ${(p) => p.$accentColor || 'rgba(184, 149, 106, 0.6)'};
    background: ${(p) => p.$accentTint || 'var(--background-primary)'};
  }

  @media (max-width: 480px) { padding: 1rem 1rem; }
`;

const KpiLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.625rem;
  display: inline-flex;
  align-items: center;
  gap: 7px;
`;

const KpiSwatch = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: ${(p) => p.$tint};
  color: ${(p) => p.$color};
  font-weight: 700;
`;

const KpiValue = styled.div`
  font-size: clamp(1.75rem, 2vw + 1rem, 2.25rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--text-primary);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  margin-bottom: 0.5rem;
`;

const KpiCaption = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-muted);
  line-height: 1.4;
`;

/* ---------- Section heading ---------- */
const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 0 0 1rem 0;
`;

const SectionTitle = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--text-primary);
  margin: 0;
`;

const SectionHint = styled.span`
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

/* ---------- Pending extensions banner ---------- */
const PendingExtensionsBanner = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.125rem 1.5rem;
  background: linear-gradient(135deg, rgba(168, 146, 84, 0.14) 0%, rgba(184, 149, 106, 0.08) 100%);
  border: 2px solid var(--primary-color);
  border-radius: 12px;
  color: inherit;
  text-decoration: none;
  margin-bottom: 2rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(184, 149, 106, 0.15);

  &:hover {
    background: linear-gradient(135deg, rgba(168, 146, 84, 0.18) 0%, rgba(184, 149, 106, 0.12) 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(184, 149, 106, 0.25);
    border-color: var(--primary-dark);
  }
`;

const BannerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--primary-color);
  color: #fff;
  flex-shrink: 0;
  font-weight: 700;
`;

const BannerBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const BannerTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.01em;
`;

const BannerCaption = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-muted);
  margin-top: 3px;
  line-height: 1.4;
`;

const BannerArrow = styled.span`
  color: var(--primary-color);
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-weight: 700;
`;

/* ---------- Quick actions (chip row) ---------- */
const QuickRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.625rem;
  margin-bottom: 2.75rem;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const QuickChip = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.875rem 1.125rem;
  background: var(--background-primary);
  border: 1.5px solid var(--primary-color);
  border-radius: 10px;
  color: var(--primary-color);
  text-decoration: none;
  font-size: 0.9375rem;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(184, 149, 106, 0.1);

  &:hover {
    background: rgba(184, 149, 106, 0.08);
    border-color: var(--primary-dark);
    color: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(184, 149, 106, 0.2);
    transform: translateY(-1px);
  }
  &:active { transform: translateY(0); }
`;

const QuickIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(184, 149, 106, 0.12);
  color: var(--primary-color);
  flex-shrink: 0;
`;

/* ---------- Manage grid ---------- */
const ManageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2.75rem;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const ManageCard = styled(motion.create(Link))`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--background-primary);
  border: 2px solid ${(p) => p.$color ? `${p.$color}33` : 'var(--border-color)'};
  border-radius: 14px;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  /* Stronger corner glow tinted by the card's accent */
  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -25%;
    width: 60%;
    height: 80%;
    background: radial-gradient(circle, ${(p) => p.$tint || 'transparent'} 0%, transparent 70%);
    opacity: 0.8;
    pointer-events: none;
    transition: opacity 0.25s ease;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 2px solid transparent;
    pointer-events: none;
    transition: border-color 0.2s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15), 0 0 0 2px ${(p) => p.$color || 'var(--primary-color)'};
    border-color: ${(p) => p.$color || 'var(--primary-color)'};
    background: ${(p) => p.$tint ? `${p.$tint}55` : 'var(--background-primary)'};
  }
  &:hover::before { opacity: 1; }
  &:hover::after { border-color: ${(p) => p.$color ? `${p.$color}33` : 'transparent'}; }
  &:hover ${'.manage-arrow'} { color: ${(p) => p.$color || 'var(--primary-color)'}; transform: translateX(2px); }
`;

const ManageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const ManageIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.$tint || 'rgba(184, 149, 106, 0.14)'};
  color: ${(p) => p.$color || 'var(--primary-color)'};
  transition: transform 0.2s ease;
  font-weight: 600;

  ${ManageCard ? `` : ''} /* keep arg shape */
`;

const ManageStat = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const ManageTitle = styled.h3`
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.015em;
`;

const ManageDesc = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--text-muted);
  font-weight: 500;
  flex: 1;
`;

const ManageFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--primary-color);
  font-size: 0.8125rem;
  font-weight: 500;
  transition: color 0.18s ease, gap 0.18s ease;

  ${ManageCard}:hover & { gap: 0.625rem; }
`;

/* ---------- Tools (compact footer-style row) ---------- */
const ToolList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.025);

  @media (max-width: 720px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const ToolItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  text-decoration: none;
  color: var(--text-primary);
  border-right: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  background: transparent;
  transition: background 0.12s ease;

  &:hover { background: var(--background-secondary); }

  &:nth-child(3n) { border-right: none; }
  &:nth-last-child(-n+3) { border-bottom: none; }

  @media (max-width: 720px) {
    &:nth-child(3n) { border-right: 1px solid var(--border-color); }
    &:nth-child(2n) { border-right: none; }
    &:nth-last-child(-n+3) { border-bottom: 1px solid var(--border-color); }
    &:nth-last-child(-n+2) { border-bottom: none; }
  }

  @media (max-width: 480px) {
    border-right: none !important;
    &:last-child { border-bottom: none; }
    &:not(:last-child) { border-bottom: 1px solid var(--border-color); }
  }
`;

const ToolIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--background-secondary);
  color: var(--text-secondary);
`;

const ToolLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToolName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const ToolHint = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

/* ============================================================== */

const ARABIC_GREETING = 'السلام عليكم';

const formatDate = () =>
  new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

const formatNumber = (n) => new Intl.NumberFormat().format(n ?? 0);

function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: null,
    totalCourses: null,
    totalStudents: null,
    totalInstructors: null,
  });
  const [pendingExtensions, setPendingExtensions] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [usersRes, coursesRes, studentsRes, instructorsRes, extRes] = await Promise.allSettled([
          apiService.getUsers(),
          apiService.getCourses(),
          apiService.getStudents(),
          apiService.getInstructors(),
          apiService.getPendingExtensionCount(),
        ]);

        if (cancelled) return;
        if (extRes.status === 'fulfilled' && extRes.value?.success) {
          setPendingExtensions(extRes.value.count || 0);
        }

        const safe = (r, key) => (r.status === 'fulfilled' && r.value?.success ? r.value[key]?.length ?? 0 : 0);

        setStats({
          totalUsers: safe(usersRes, 'users'),
          totalCourses: safe(coursesRes, 'courses'),
          totalStudents: safe(studentsRes, 'students'),
          totalInstructors: safe(instructorsRes, 'instructors'),
        });
      } catch (error) {
        logger.error('Error fetching dashboard data', { error });
      }
    };

    load();
    if (window.innerWidth <= 768) window.scrollTo(0, 0);

    return () => { cancelled = true; };
  }, []);

  const firstName = useMemo(() => {
    const raw = user?.firstName || user?.first_name || user?.name || user?.email?.split('@')[0] || '';
    return String(raw).split(' ')[0];
  }, [user]);

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.32, delay, ease: [0.22, 1, 0.36, 1] },
  });

  const kpis = [
    { label: 'Total users',    value: stats.totalUsers,       caption: 'All accounts',      icon: <FiUsers size={12} />,     accent: ACCENT.indigo },
    { label: 'Active courses', value: stats.totalCourses,     caption: 'Currently offered', icon: <FiBookOpen size={12} />,  accent: ACCENT.gold },
    { label: 'Students',       value: stats.totalStudents,    caption: 'Enrolled learners', icon: <FiUser size={12} />,      accent: ACCENT.sage },
    { label: 'Instructors',    value: stats.totalInstructors, caption: 'Teaching staff',    icon: <FiBriefcase size={12} />, accent: ACCENT.teal },
  ];

  const quickActions = [
    { to: '/admin/users',           label: 'Add user',         icon: <FiUserPlus size={15} /> },
    { to: '/admin/courses',         label: 'New course',       icon: <FiPlus size={15} /> },
    { to: '/admin/attendance',      label: 'Take attendance',  icon: <FiCheckCircle size={15} /> },
    { to: '/admin/promo-codes',     label: 'Promo code',       icon: <FiTag size={15} /> },
  ];

  const manageCards = [
    {
      to: '/admin/users',
      icon: <FiUsers size={18} />,
      title: 'People',
      desc: 'Students, instructors, and admin accounts.',
      stat: stats.totalUsers != null ? `${formatNumber(stats.totalUsers)} total` : '—',
      accent: ACCENT.indigo,
    },
    {
      to: '/admin/courses',
      icon: <FiBookOpen size={18} />,
      title: 'Courses',
      desc: 'Create, edit, and publish course content.',
      stat: stats.totalCourses != null ? `${formatNumber(stats.totalCourses)} active` : '—',
      accent: ACCENT.gold,
    },
    {
      to: '/admin/electives',
      icon: <FiBookOpen size={18} />,
      title: 'Electives',
      desc: 'Manage elective offerings and assignments.',
      stat: '',
      accent: ACCENT.terracotta,
    },
    {
      to: '/admin/attendance',
      icon: <FiCalendar size={18} />,
      title: 'Attendance',
      desc: 'Track sessions and student attendance.',
      stat: '',
      accent: ACCENT.sage,
    },
    {
      to: '/admin/reports',
      icon: <FiBarChart2 size={18} />,
      title: 'Reports',
      desc: 'Enrollment, performance, and revenue insights.',
      stat: '',
      accent: ACCENT.teal,
    },
    {
      to: '/admin/pricing',
      icon: <FiDollarSign size={18} />,
      title: 'Pricing',
      desc: 'Course pricing and payment configuration.',
      stat: '',
      accent: ACCENT.amber,
    },
  ];

  const tools = [
    { to: '/admin/promo-codes',         icon: <FiTag size={15} />,         name: 'Promo codes',        hint: 'Discount and referral codes' },
    { to: '/admin/bulk-pricing',        icon: <FiPackage size={15} />,     name: 'Bulk pricing',       hint: 'Multi-course bundles' },
    { to: '/admin/additional-products', icon: <FiShoppingBag size={15} />, name: 'Additional products',hint: 'Sponsorships, donations' },
    { to: '/admin/purchase-lookup',     icon: <FiSearch size={15} />,      name: 'Purchase lookup',    hint: 'Find an order or receipt' },
    { to: '/admin/import-export',       icon: <FiUpload size={15} />,      name: 'Import / export',    hint: 'Bulk data operations' },
    { to: '/admin/deleted-items',       icon: <FiTrash2 size={15} />,      name: 'Deleted items',      hint: 'Restore or purge' },
    { to: '/admin/permissions',         icon: <FiUsers size={15} />,       name: 'Permissions',        hint: 'Grant content access' },
  ];

  return (
    <Page>
      <Hero>
        <HeroEyebrow>{formatDate()}</HeroEyebrow>
        <HeroTitle>
          <HeroGreetingAr lang="ar" aria-label="Assalamu Alaikum">
            {ARABIC_GREETING}
          </HeroGreetingAr>
          {firstName && <HeroGreetingName>{firstName}.</HeroGreetingName>}
        </HeroTitle>
        <HeroSub>
          Here's a snapshot of the Faith Insight Portal and quick access to everything you manage.
        </HeroSub>
      </Hero>
      <HeroDivider />

      <KpiGrid>
        {kpis.map((k, i) => (
          <Kpi key={k.label} $accent={k.accent.solid} {...fadeUp(i * 0.04)}>
            <KpiLabel>
              <KpiSwatch $tint={k.accent.tint} $color={k.accent.solid}>{k.icon}</KpiSwatch>
              {k.label}
            </KpiLabel>
            <KpiValue>{k.value == null ? '—' : formatNumber(k.value)}</KpiValue>
            <KpiCaption>{k.caption}</KpiCaption>
          </Kpi>
        ))}
      </KpiGrid>

      {pendingExtensions > 0 && (
        <PendingExtensionsBanner to="/admin/extensions">
          <BannerIcon>
            <FiBell size={18} />
          </BannerIcon>
          <BannerBody>
            <BannerTitle>
              {pendingExtensions} pending extension {pendingExtensions === 1 ? 'request' : 'requests'}
            </BannerTitle>
            <BannerCaption>Review and respond on the Extensions page.</BannerCaption>
          </BannerBody>
          <BannerArrow><FiArrowUpRight size={16} /></BannerArrow>
        </PendingExtensionsBanner>
      )}

      <SectionHead>
        <SectionTitle>Quick actions</SectionTitle>
      </SectionHead>
      <QuickRow>
        {quickActions.map((a) => (
          <QuickChip key={a.label} to={a.to}>
            <QuickIcon>{a.icon}</QuickIcon>
            {a.label}
          </QuickChip>
        ))}
      </QuickRow>

      <SectionHead>
        <SectionTitle>Manage</SectionTitle>
        <SectionHint>Six core areas</SectionHint>
      </SectionHead>
      <ManageGrid>
        {manageCards.map((c, i) => (
          <ManageCard
            key={c.to}
            to={c.to}
            $tint={c.accent.tint}
            $color={c.accent.solid}
            {...fadeUp(0.1 + i * 0.04)}
          >
            <ManageHeader>
              <ManageIcon $tint={c.accent.tint} $color={c.accent.solid}>{c.icon}</ManageIcon>
              {c.stat && <ManageStat>{c.stat}</ManageStat>}
            </ManageHeader>
            <ManageTitle>{c.title}</ManageTitle>
            <ManageDesc>{c.desc}</ManageDesc>
            <ManageFooter className="manage-arrow">
              Open
              <FiArrowUpRight size={14} />
            </ManageFooter>
          </ManageCard>
        ))}
      </ManageGrid>

      <SectionHead>
        <SectionTitle>Tools</SectionTitle>
        <SectionHint>Less-frequent operations</SectionHint>
      </SectionHead>
      <ToolList>
        {tools.map((t) => (
          <ToolItem key={t.to} to={t.to}>
            <ToolIcon>{t.icon}</ToolIcon>
            <ToolLabel>
              <ToolName>{t.name}</ToolName>
              <ToolHint>{t.hint}</ToolHint>
            </ToolLabel>
          </ToolItem>
        ))}
      </ToolList>
    </Page>
  );
}

export default AdminDashboard;
